import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { type AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { catchError, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../core/i18n/translation.service';
import type { AssetAssignmentDto, AssetDetailsDto, AssetStatus } from '../../../../core/models/assets/assets.dto';
import { AssetsService } from '../../../../core/services/assets.service';
import { ToastFeedbackService } from '../../../../core/services/toast-feedback.service';
import { UserLookupSelectComponent } from '../../../../shared/components/user-lookup-select/user-lookup-select.component';
import { AdminBackLinkComponent } from '../../../../shared/components/admin-back-link/admin-back-link.component';
import { WnDatePickerComponent } from '../../../../shared/components/wn-date-picker/wn-date-picker.component';
import { WnRequiredFieldMarkerDirective } from '../../../../shared/directives/wn-required-field-marker.directive';
import { StatusChip } from '../../../../shared/components/status-chip/status-chip';
import { AssetLossDialogComponent } from './asset-loss-dialog/asset-loss-dialog.component';
import type { AssetChargeInput } from '../../../../core/models/assets/asset-charges.dto';
import { AssetChargesPanelComponent } from '../asset-charges/asset-charges-panel/asset-charges-panel.component';
import { pickLocalizedTitle } from './asset-titles';

import { WnFocusInvalidOnSubmitDirective } from '../../../../shared/directives/wn-focus-invalid-on-submit.directive';
@Component({
  selector: 'app-asset-detail-page',
  standalone: true,
  imports: [WnFocusInvalidOnSubmitDirective,
    WnRequiredFieldMarkerDirective,
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    ButtonModule,
    UserLookupSelectComponent,
    AdminBackLinkComponent,
    WnDatePickerComponent,
    StatusChip,
    AssetLossDialogComponent,
    AssetChargesPanelComponent,
  ],
  templateUrl: './asset-detail-page.component.html',
  styleUrl: './asset-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetDetailPageComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly assets = inject(AssetsService);
  private readonly translation = inject(Translation);
  private readonly toast = inject(ToastFeedbackService);

  protected readonly asset = signal<AssetDetailsDto | null>(null);
  protected readonly loading = signal(true);
  protected readonly actionLoading = signal(false);
  protected readonly loadError = signal<string | null>(null);
  protected readonly actionError = signal<string | null>(null);
  /** Set while the lost/damaged charge dialog is open. */
  protected readonly lossMode = signal<'lost' | 'damaged' | null>(null);
  protected readonly returnStatuses: AssetStatus[] = [1, 3, 4, 5];
  protected readonly activeAssignment = computed(() =>
    this.asset()?.assignmentHistory.find((row) => !row.returnedAt && (row.assignmentStatus === 1 || row.assignmentStatus === 3)) ?? null,
  );

  protected readonly assignForm = this.fb.group({
    assignedToUserId: this.fb.control('', { validators: [Validators.required] }),
    expectedReturnDate: this.fb.control(''),
    conditionOnAssign: this.fb.control(''),
    adminNotes: this.fb.control(''),
  });

  protected readonly returnForm = this.fb.group({
    resultingAssetStatus: this.fb.control<AssetStatus>(1, { validators: [Validators.required] }),
    conditionOnReturn: this.fb.control(''),
    adminNotes: this.fb.control(''),
  });

  protected readonly maintenanceForm = this.fb.group({
    maintenanceDate: this.fb.control(this.today(), { validators: [Validators.required] }),
    providerTitle: this.fb.control(''),
    cost: this.fb.control<number | null>(null),
    description: this.fb.control(''),
  });

  constructor() {
    this.route.paramMap
      .pipe(
        map((pm) => pm.get('id')),
        distinctUntilChanged(),
        switchMap((id) => {
          this.loading.set(true);
          this.loadError.set(null);
          this.actionError.set(null);
          if (!id) {
            this.loading.set(false);
            return of(null);
          }
          return this.assets.getAsset(id).pipe(
            catchError((err) => {
              this.loadError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
              return of(null);
            }),
            finalize(() => this.loading.set(false)),
          );
        }),
      )
      .subscribe((asset) => {
        this.asset.set(asset);
      });
  }

  protected title(asset: AssetDetailsDto): string {
    return pickLocalizedTitle(asset.titleEn, asset.titleAr, this.isArabic());
  }

  protected categoryTitle(asset: AssetDetailsDto): string {
    return pickLocalizedTitle(asset.assetCategoryTitleEn, asset.assetCategoryTitleAr, this.isArabic());
  }

  protected branchTitle(asset: AssetDetailsDto): string {
    return pickLocalizedTitle(asset.currentBranchTitleEn, asset.currentBranchTitleAr, this.isArabic());
  }

  private isArabic(): boolean {
    return this.translation.getCurrentLang().toLowerCase().startsWith('ar');
  }

  protected statusLabel(status: number): string {
    return `ASSETS.STATUS.${status}`;
  }

  protected assignmentStatusLabel(status: number): string {
    return `ASSETS.ASSIGNMENT_STATUS.${status}`;
  }

  protected maintenanceStatusLabel(status: number): string {
    return `ASSETS.MAINTENANCE_STATUS.${status}`;
  }

  protected assign(): void {
    const asset = this.asset();
    if (!asset || this.assignForm.invalid) {
      this.assignForm.markAllAsTouched();
      return;
    }
    const raw = this.assignForm.getRawValue();
    this.runAction(
      this.assets.assignAsset({
        assetId: asset.id,
        assignedToUserId: raw.assignedToUserId,
        expectedReturnDate: this.nullIfEmpty(raw.expectedReturnDate),
        conditionOnAssign: this.nullIfEmpty(raw.conditionOnAssign),
        adminNotes: this.nullIfEmpty(raw.adminNotes),
      }),
      'ASSETS.ASSIGN_DONE',
      this.assignForm,
    );
  }

  protected returnActiveAssignment(): void {
    const assignment = this.activeAssignment();
    if (!assignment || this.returnForm.invalid) {
      this.returnForm.markAllAsTouched();
      return;
    }
    const raw = this.returnForm.getRawValue();
    this.runAction(
      this.assets.returnAsset(assignment.id, {
        resultingAssetStatus: raw.resultingAssetStatus,
        conditionOnReturn: this.nullIfEmpty(raw.conditionOnReturn),
        adminNotes: this.nullIfEmpty(raw.adminNotes),
      }),
      'ASSETS.RETURN_DONE',
      this.returnForm,
    );
  }

  protected scheduleMaintenance(): void {
    const asset = this.asset();
    if (!asset || this.maintenanceForm.invalid) {
      this.maintenanceForm.markAllAsTouched();
      return;
    }
    const raw = this.maintenanceForm.getRawValue();
    this.runAction(
      this.assets.scheduleMaintenance({
        assetId: asset.id,
        maintenanceDate: raw.maintenanceDate,
        providerTitle: this.nullIfEmpty(raw.providerTitle),
        cost: raw.cost,
        description: this.nullIfEmpty(raw.description),
        status: 1,
      }),
      'ASSETS.MAINTENANCE_DONE',
      this.maintenanceForm,
    );
  }

  protected confirmLoss(charge: AssetChargeInput | null): void {
    const mode = this.lossMode();
    this.lossMode.set(null);
    if (mode) this.runAssetAction(mode, charge ?? undefined);
  }

  protected runAssetAction(action: 'retire' | 'lost' | 'damaged' | 'maintenance' | 'complete-maintenance', charge?: AssetChargeInput): void {
    const asset = this.asset();
    if (!asset) return;
    const request =
      action === 'retire'
        ? this.assets.retire(asset.id)
        : action === 'lost'
          ? this.assets.markLost(asset.id, charge)
          : action === 'damaged'
            ? this.assets.markDamaged(asset.id, charge)
            : action === 'complete-maintenance'
              ? this.assets.completeAssetMaintenance(asset.id)
              : this.assets.sendToMaintenance(asset.id);
    this.runAction(request, 'ASSETS.ACTION_DONE');
  }

  /**
   * `submittedForm` is the card form the action came from. Once the server accepted it, its values are no longer pending
   * edits: reset it to its defaults so the page's discard guard (shared/guards/pending-changes.guard.ts) sees it clean
   * again and only fires for edits made after the action (WN-1511). A failed action keeps the form as typed.
   */
  private runAction(
    request: { subscribe: (handlers: { next: () => void; error: (err: unknown) => void }) => void },
    successKey: string,
    submittedForm?: AbstractControl,
  ): void {
    this.actionLoading.set(true);
    this.actionError.set(null);
    request.subscribe({
      next: () => {
        submittedForm?.reset();
        this.toast.successKey(successKey);
        this.reloadCurrentAsset();
        this.actionLoading.set(false);
      },
      error: (err) => {
        this.actionError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
        this.actionLoading.set(false);
      },
    });
  }

  private reloadCurrentAsset(): void {
    const id = this.asset()?.id;
    if (!id) return;
    this.assets.getAsset(id).subscribe((asset) => this.asset.set(asset));
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private nullIfEmpty(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
}
